import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from 'react';
import { unstable_batchedUpdates } from 'react-dom';
import useWizardStore, {
} from '@zs/useWizardStore';
import * as common from '@/common';
import * as elements from '@svg/elements';
import cache from '@/cache';
import {
  octSettings,
  octBoundsDeg,
} from '@/imageFiles';
import {
  Coord,
  lerpColor,
  getTDBColor,
  hexToRGB,
  rgbToHex,
  LayoutRow,
  ResultRow,
  getDirection,
  getArrayStats,
  getFirstID,
  getStrokeColor,
  SVGtoDOMMatrix,
  generateSpindle,
  checkDistance,
  LocUpdate,
} from '@t/LayoutTypes';
import {
  LocData,
  MarginData,
} from '@svg/elements';
import VisualFieldCanvas from './VisualFieldCanvas';
import TransformGroup from './TransformGroup';
import { LabelSlider } from './LabelSlider';
import { RetinalOverlay } from './RetinalOverlay';
import { CroppedImage } from './CroppedImage';
import { VesselMarker } from './VesselMarker';
import { useImageCache } from '@/useImageCache';
import { useOctViewer } from '@svg/useOctViewer';
import BScroller from '@svg/BScroller';

interface HomeEditorProps {
  eye: 'OD' | 'OS';
  templateCSV: LayoutRow[];
  enableCyclo: boolean;
  onComplete: (isComplete: boolean, locs?: LocUpdate[]) => void;
}
export const HomeEditor = ({
  eye,
  templateCSV,
  enableCyclo,
  onComplete,
} : HomeEditorProps) : JSX.Element => {
  const localizerCSV = useWizardStore(state => state.localizerCSV);
  const localizerResult = useWizardStore(state => state.localizerResult);
  const vascularCoords = useWizardStore(state => state.vascularCoords);

  const cacheLoaded = useWizardStore(state => state.cacheLoaded);
  const registration = useWizardStore(state => state.registrations[eye]);
  const fovea = useWizardStore(state => state.fovealPit[eye]);


  const [blend, setBlend] = useState(0.8);
  const [showData, setShowData] = useState(true);
  const [showSpindles, setShowSpindles] = useState(false);

  const [step, setStep] = useState(0);
  const [didAction, setDidAction] = useState(true);
  const prlMatrix = useRef<DOMMatrixReadOnly>();
  const setPRL = useWizardStore(state => state.setPRL);
  const setCyclo = useWizardStore(state => state.setCyclo);
  const pgEdits = useRef(new Map<number, Coord>());
  const arrayEdits = useRef(new Map<string, Coord>());

  const parentG = useRef<SVGGElement>(null);
  const [translation, setTranslation] = useState<Coord>({ x: 0, y: 0 });
  const [rotation, setRotation] = useState(0);
  const [didEditArrays, setDidEditArrays] = useState(false);
  const [marks, setMarks] = useState<number[]>([]);

  const {
    slice: { bSlice, setBSlice, handleBSlice },
    index: { bIndex, setBIndex, handleBScroll },
    enface: scanCoord,
  } = useOctViewer(eye);

  const animDiv = useRef<HTMLDivElement>(null);

  // init layout
  const {
    locData,
    homeLocsByGroup,
    marginByGroup,
  } = useMemo(() => {
    const layoutByID = new Map<number, LayoutRow>();
    const resultByID = new Map<number, ResultRow>();
    const idsByGroup = new Map<string, number[]>();
    // const localizerArrayByGroup = new Map<string, Coord[]>();
    const marginByGroup = new Map<string, MarginData>();
    const homeLocsByGroup = new Map<string, LocData[]>();

    const lr = eye == 'OD' ? 'right' : 'left';
    const eyeLower = eye.toLowerCase();
    const myCSV = localizerCSV.filter(r => r.stimulus_eye == lr);
    const myResult = localizerResult.filter(r => r.stim_eye == eyeLower);
    for (const r of myCSV) {
      layoutByID.set(r.stimulus_location_id, r);
      if (!idsByGroup.has(r.array_id))
        idsByGroup.set(r.array_id, []);
      idsByGroup.get(r.array_id)!.push(r.stimulus_location_id);
    }
    for (const r of myResult)
      resultByID.set(r.locID, r);

    const locData: LocData[] = myCSV
      .map(r => {
        const resRow = resultByID.get(r.stimulus_location_id);
        const colors = resRow
          ? getColors(resRow.metric, resRow.metric_name)
          : { fill: 'transparent', stroke: '#f00' };
        return {
          id: r.stimulus_location_id,
          arrayID: r.array_id,
          diameter: r.stimulus_diameter_deg,
          fill: colors.fill,
          stroke: colors.stroke,
          x: resRow ? resRow.x_deg : r.stimulus_x_deg,
          y: resRow ? resRow.y_deg : r.stimulus_y_deg,
      }});
    const myGroups = [...idsByGroup.keys()].filter(k => homeGroups.includes(k));
    for (const grp of myGroups) {
      const [type, dir] = grp.split('-');
      const ids = idsByGroup.get(grp)!;
      const homies = locData.filter(l => ids.includes(l.id)).map(l => uncoloredLocData(l));
      if (didEditArrays && arrayEdits.current.has(grp)) {
        const edit = arrayEdits.current.get(grp)!;
        for (const h of homies) {
          h.x += edit.x;
          h.y += edit.y;
        }
      }
      const stats = getArrayStats(homies);
      const vec = getDirection(dir, eye);
      if (type == 'BS') {
        if (dir == 'C' || dir == 'T' || dir == 'N')
          homeLocsByGroup.set(grp, homies);
        if (dir != 'C')
          marginByGroup.set(grp, { arrayID: grp, vertical: vec.x != 0, stroke: getStrokeColor(type), ...stats.center });
      }
      else if (type == 'GA') {
        const spindle = generateSpindle(vec, stats.center, stats.interval);
        let firstID = getFirstID(ids);
        const crem = firstID < 0 ? -1 : 1;
        // const shit = layoutByID.has(firstID);
        // const piss = layoutByID.get(firstID);
        // const diam: number = layoutByID.get(firstID)!.stimulus_diameter_deg;
        homeLocsByGroup.set(grp, spindle.map((s, i) => ({
          id: firstID + i * crem,
          arrayID: grp,
          diameter: homies[0].diameter,
          ...s,
        })));
        marginByGroup.set(grp, { arrayID: grp, vertical: vec.x != 0, stroke: getStrokeColor(type), ...stats.center });
      }
      else if (type == 'HP')
        homeLocsByGroup.set(grp, homies);
    }
    // add stuff in template which was not in localizer
    const tSet = new Set(templateCSV.map(r => r.array_id));
    for (const grp of [...tSet].filter(k => !homeLocsByGroup.has(k))) {
      const rows = templateCSV.filter(r => r.array_id == grp && r.stimulus_eye == lr);
      for (const t of rows) {
        if (!homeLocsByGroup.has(t.array_id))
          homeLocsByGroup.set(t.array_id, []);
        homeLocsByGroup.get(t.array_id)!.push({
          id: t.stimulus_location_id,
          arrayID: t.array_id,
          diameter: t.stimulus_diameter_deg,
          x: t.stimulus_x_deg,
          y: t.stimulus_y_deg,
        });
      }
    }
    for (const h of [...homeLocsByGroup.values()].flat()) {
      for (const mark of vascularCoords[eye]) {
        h.isMarked = checkDistance(mark, h, h.diameter/3);
        if (h.isMarked) break;
      }
    }

    return {
      locData,
      homeLocsByGroup,
      marginByGroup,
    };
  }, [templateCSV, localizerCSV, localizerResult, vascularCoords, eye, didEditArrays]);

  const imgMemo = useMemo(() => {
    if (!cacheLoaded || cache.imageCount < 1)
      return undefined;
    // reg was done in raw pixel space, as unfixed->fixed. so layers are scaled together, as unfixed->local
    const imgCache = cache.images[eye]!;
    const overlayScale = fullDeg / imgCache.RED.crop.width;
    const fovDeg = elements.getFoveaDeg(fovea, imgCache.BSCAN.crop.width);
    return {
      cache: imgCache,
      overlayScale,
      fovDeg,
    };
  }, [eye, cacheLoaded, fovea]);

  const handleEditPG = useCallback((id: string, value: Coord | number, ctm: DOMMatrixReadOnly) => {
    const locID = parseInt(id);
    pgEdits.current.set(locID, value as Coord);
    setDidAction(true);
  }, []);

  if (!cacheLoaded)
    return <div>Waiting for imaging...</div>;

  const mySteps = enableCyclo ? stepsStudy : stepsNonStudy;
  const currentStep = mySteps[step];
  let helpText = 'You have completed the layout for this eye! You may click Next at the bottom to proceed.';
  if (step < mySteps.length) {
    helpText = currentStep.help;
  }

  const lr = eye == 'OD' ? 'right' : 'left';
  const bsc = localizerCSV.find(r => r.stimulus_eye == lr && r.array_id == 'BS-C')!;
  let manipulator: JSX.Element | undefined = undefined;
  if (currentStep?.action == 'translate') {
    const color = getStrokeColor(enableCyclo ? 'GA' : 'BS');
    const x = enableCyclo ? 0 : bsc.stimulus_x_deg;
    const y = enableCyclo ? 0 : bsc.stimulus_y_deg;
    const r = enableCyclo ? 3 : 5;
    manipulator = <TransformGroup key='manip-t' id='prl' mode='translate' onFrame={handlePRLDrag} onUse={handlePRL}>
      <circle cx={x} cy={y} r={r} fill='transparent' stroke={color} strokeWidth={strokeWidthDeg} opacity={0.6} />
    </TransformGroup>
  }
  else if (currentStep?.action == 'rotate') {
    manipulator = <g transform={`translate(${translation.x}, ${translation.y})`}>
      <TransformGroup key='manip-r' id='prl' mode='rotate' onFrame={handleCycloDrag} onUse={handleCyclo}>
        <circle cx={bsc.stimulus_x_deg} cy={bsc.stimulus_y_deg} r={5} fill='transparent' stroke={getStrokeColor('BS')} strokeWidth={strokeWidthDeg} opacity={0.6} />
      </TransformGroup>
    </g>
  }
  else if (currentStep?.action == 'arrays') {
    manipulator = <g key='edit-arrays' transform={`translate(${translation.x}, ${translation.y}) rotate(${rotation})`} strokeWidth={strokeWidthDeg}>
      {[...homeLocsByGroup.entries()].filter(([grp, locs]) => grp.startsWith('BS') || grp.startsWith('GA')).map(([grp, locs]) => {
        const arrayType = grp.split('-')[0];
        const color = getStrokeColor(arrayType);
        const stats = getArrayStats(locs);
        const snapParams = {
          interval: scanIntervalDeg,
          offset: scanOffsetDeg - imgMemo!.fovDeg.y - locs[0].y,
        };
        return <TransformGroup key={`manip-${grp}`} id={grp} mode='translate' snap={snapParams} onFrame={handleArrayDrag} onUse={handleArray}>
          <rect x={stats.bounds.minX-1} y={stats.bounds.minY-1} width={stats.w+2} height={stats.h+2} fill='transparent' strokeWidth={0} />
          {locs.map(loc => 
            <circle key={loc.id} cx={loc.x} cy={loc.y} r={loc.diameter/2} fill='transparent' stroke={color} />
          )}
          {grp != 'BS-C' && elements.renderMargin(marginByGroup.get(grp)!)}
        </TransformGroup>
      })}
    </g>
  }

  let hideLayerControls = false;
  let dataLayerVis = showData ? 'visible' : 'collapse';
  let homeLayerVis = showSpindles ? 'visible' : 'collapse';
  let marginVis = 'visible';
  if (currentStep?.action == 'arrays') {
    hideLayerControls = true;
    dataLayerVis = 'visible';
    homeLayerVis = 'collapse';
    marginVis = 'collapse';
  }
  else if (currentStep?.action == 'mark') {
    hideLayerControls = true;
    dataLayerVis = 'collapse';
    homeLayerVis = 'visible';
  }
  if (currentStep?.action == 'pg') {
    hideLayerControls = true;
    dataLayerVis = 'collapse';
    homeLayerVis = 'visible';
  }

  function handleBlendSlider(v: number) {
    setBlend(v);
  }

  function handlePRLDrag(id: string, v: Coord | number) {
    setTranslation(v as Coord);
  }

  function handlePRL(id: string, v: Coord | number, m: DOMMatrixReadOnly) {
    prlMatrix.current = m;
    setDidAction(true);
  }

  function handleCycloDrag(id: string, v: Coord | number) {
    setRotation(v as number);
  }

  function handleCyclo(id: string, v: Coord | number, m: DOMMatrixReadOnly) {
    prlMatrix.current = m;
    setDidAction(true);
  }

  function handleArrayDrag(id: string, v: Coord | number) {
  }

  function handleArray(id: string, v: Coord | number, m: DOMMatrixReadOnly) {
    const c = v as Coord;
    if (Math.abs(c.x) > 0.01 || Math.abs(c.y) > 0.01)
      arrayEdits.current.set(id, c);
    setDidAction(true);
  }

  function handleMarker(id: number, marked: boolean, c: Coord) {
    if (marked)
      setMarks(m => [...m, id]);
    else setMarks(m => m.filter(i => i != id));
  }

  function handleDataToggle(e) {
    setShowData(!showData);
  }

  function handleHomeToggle(e) {
    setShowSpindles(!showSpindles);
  }

  function handleEditClick() {
    setStep(step+1);
    setDidAction(false);
    if (mySteps[step+1].action == 'arrays') {
      setShowData(true);
      setShowSpindles(false);
    }
    animDiv.current!.className = 'animate-[flash-in_0.5s]';
    setTimeout(() => {
      animDiv.current!.className = '';
    }, 500);
  }

  function handleOKClick() {
    let nexti = step + 1;
    if (mySteps[step].action.startsWith('idle'))
      nexti++;
    else if (mySteps[step].action == 'arrays') {
      if (arrayEdits.current.size > 0)
        setDidEditArrays(true);
      else nexti++;
    }
    if (nexti < mySteps.length && mySteps[nexti].action == 'mark' && arrayEdits.current.size == 0)
      nexti++;
    setStep(nexti);
    if (nexti >= mySteps.length) {
      setShowData(false);
      setShowSpindles(true);
      setPRL(eye, translation);
      setCyclo(eye, rotation);
      const pg = applyEdits(homeLocsByGroup.get('PG')!, pgEdits.current);
      const nonEdited: LocData[] = [];
      const edited: LocData[] = [];
      for (const [k, v] of homeLocsByGroup) {
        if (k == 'PG') continue;
        if (!arrayEdits.current.has(k)) {
          nonEdited.push(...v);
          continue;
        }
        for (const loc of v) {
          const edit = arrayEdits.current.get(k)!;
          edited.push({
            ...loc,
            // x: loc.x + edit.x,
            // y: loc.y + edit.y,
          });
        }
      }
      const up = [...nonEdited, ...edited, ...pg].map(loc => ({
        id: loc.id,
        arrayID: loc.arrayID,
        x: loc.x,
        y: loc.y
      }));
      for (const u of up) {
        if (!arrayEdits.current.has(u.arrayID))
          continue;
        (u as LocUpdate).isMarked = marks.includes(u.id);
      }
      onComplete(true, up);
    }
    else if (mySteps[nexti].action == 'idleArrays') {
      setShowData(true);
      setShowSpindles(true);
    }
    else if (mySteps[nexti].action == 'pg')
      setDidAction(false);
    animDiv.current!.className = 'animate-[flash-in_0.5s]';
    setTimeout(() => {
      animDiv.current!.className = '';
    }, 500);
  }

  function handleUndoClick() {
    prlMatrix.current = new DOMMatrixReadOnly();
    pgEdits.current.clear();
    arrayEdits.current.clear();
    setDidEditArrays(false);
    setMarks([]);
    setShowData(true);
    setShowSpindles(false);
    setTranslation({ x: 0, y: 0 });
    setRotation(0);
    setStep(0);
    onComplete(false);
    animDiv.current!.className = 'animate-[flash-in_0.5s]';
    setTimeout(() => {
      animDiv.current!.className = '';
    }, 500);
  }

  const visLocs = locData.filter(l => l.arrayID != 'HP');
  let visHome = [...homeLocsByGroup.values()].flat().filter(l => l.arrayID != 'HP' && l.arrayID != 'PG');
  if (currentStep?.action == 'idleArrays')
    visHome = visHome.filter(l => l.arrayID.startsWith('BS') || l.arrayID.startsWith('GA'));
  const pgGroup = homeLocsByGroup.get('PG')!;
  const pgEdited = applyEdits(pgGroup, pgEdits.current);
  const staticMargins = [...marginByGroup.entries()].filter(([k, v]) => !enableCyclo || (k != 'BS-N' && k != 'BS-T')).map(([k, v]) => v);

  const markers: JSX.Element[] = [];
  const canMark = currentStep?.action == 'mark';
  for (const k of arrayEdits.current.keys()) {
    for (const h of homeLocsByGroup.get(k)!) {
      h.isMarked = marks.includes(h.id);
      markers.push(<VesselMarker key={h.id} initValue={h.isMarked} id={h.id} x={h.x} y={h.y} r={h.diameter/2} onToggle={handleMarker} />);
    }
  }

  const bCrop = imgMemo.cache.BSCAN.crop;
  const bscanCt = imgMemo.cache.BSCAN.urls.length;

  return (
    <div className='flex gap-2'>
      <div className='flex flex-col gap-1'>
        <VisualFieldCanvas width={600} height={600} pixelsPerDegree={10}>
          <g ref={parentG} transform='scale(10)' style={{ pointerEvents: 'none' }}>
            <g key='imaging' transform={`translate(${translation.x}, ${translation.y}) rotate(${rotation})`}>
              <RetinalOverlay eye={eye} scanIndex={bIndex} bscanX={bSlice} blend={blend} />
            </g>
            <g key='loc-data' visibility={dataLayerVis} strokeWidth={strokeWidthDeg}>
              {visLocs.map(elements.renderLoc)}
            </g>
            {manipulator}
            <g key='home-sticky' transform={`translate(${translation.x}, ${translation.y}) rotate(${rotation})`}>
              <g key={'home-pg'} fill='#399' stroke='#7dd' strokeWidth={strokeWidthDeg}>
                {currentStep?.action == 'pg' && pgGroup.map(l => renderPG(l, imgMemo.fovDeg.y, handleEditPG))}
              </g>
              <g key='home-locs' visibility={homeLayerVis} fill='none' stroke='#fff' strokeWidth={strokeWidthDeg}>
                {!currentStep && pgEdited.map(elements.renderLoc)}
                {visHome.map(elements.renderLoc)}
                {visHome.filter(l => l.isMarked).map(elements.renderMark)}
                <g key='markers' fill='transparent' stroke='white' strokeWidth={strokeWidthDeg} style={{ pointerEvents: canMark ? 'auto' : 'none' }}>
                  {markers}
                </g>
              </g>
              <g key='margins' visibility={marginVis} strokeWidth={strokeWidthDeg}>
                {staticMargins.map(elements.renderMargin)}
              </g>
            </g>
          </g>
        </VisualFieldCanvas>
        <LabelSlider id='blend' label='FAF Blend' initValue={80} onValue={handleBlendSlider} />
      </div>
      <div className='flex flex-col flex-1 gap-1'>
        <div className={`flex flex-row gap-1`}>
          <div className='flex flex-col gap-1'>
            <div className='text-xs'>Scan No.</div>
            <input
              type='number'
              value={bIndex + 1}
              onChange={handleBScroll}
              step={1}
              min={1}
              max={bscanCt}
              className='w-12 border rounded-md'
            />
            <input
              type='range'
              value={bIndex + 1}
              onChange={handleBScroll}
              step={1}
              min={1}
              max={bscanCt}
              style={{ writingMode: 'vertical-rl', direction: 'rtl' }}
              className='grow'
            />
          </div>
          <svg
            className='h-[300px]'
            viewBox={`${bCrop.x} ${bCrop.y} ${bCrop.width} ${bCrop.height}`}
          >
            <BScroller eye={eye} index={bIndex} onSlice={handleBSlice} />
          </svg>
        </div>
        <div className='text-sm'>Click and drag on the the B-scan above to place a corresponding vertical line on the view to the left.</div>
        <div ref={animDiv}>
          <article className='my-8'>
            <p className='text-wrap'>{step+1}. {helpText}</p>
            {currentStep && <p className='text-sm italic'>{currentStep.tooltip}</p>}
            <p className='text-sm italic'>Click and drag empty areas to pan the view. Scroll the mouse wheel to zoom the view.</p>
            <p className='text-sm italic'>Click and drag the B-scan to place a vertical line onto the infrared image.</p>
          </article>
          <div className='flex flex-row gap-1'>
            {currentStep && (
            <button onClick={handleOKClick} disabled={!didAction} className="btn">{currentStep.confirmButton}</button>
            )}
            {!currentStep && (
            <button disabled={true} className="btn">Finished</button>
            )}
            {currentStep?.editButton && (
            <button onClick={handleEditClick} className="btn bg-purple-500">{currentStep.editButton}</button>
            )}
            {(!currentStep) && (
            <button onClick={handleUndoClick} className="btn bg-teal-600">{!currentStep ? 'Undo All' : 'currentStep.undoButton'}</button>
            )}
          </div>
        </div>
        <hr />
        <div style={{visibility: hideLayerControls? 'hidden' : 'visible'}}>
          <input type='checkbox' id='show-data' checked={showData} onChange={handleDataToggle} className='mr-2' />
          <label htmlFor='show-data'>Show Localizer Data</label>
        </div>
        <div style={{visibility: hideLayerControls? 'hidden' : 'visible'}}>
          <input type='checkbox' id='show-spindles' checked={showSpindles} onChange={handleHomeToggle} className='mr-2' />
          <label htmlFor='show-spindles'>Show Home Locations</label>
        </div>
      </div>
    </div>
  );
}




const fullDeg = octSettings.enfaceAngle;
const strokeWidthDeg = elements.strokeWidthDeg;
const scanIntervalDeg = octSettings.patternSize.h / (octSettings.totalBscans - 1);
const scanOffsetDeg = octSettings.totalBscans % 2 == 0 ? (scanIntervalDeg / 2) : 0;

function getColors(metric: number, metricName: string) {
  let fill = '#000000';
  if (metricName == 'sensitivity_tdB') fill = getTDBColor(metric);
  else if (metricName == 'percent_seen') fill = rgbToHex(lerpColor(metric/100, [0, 0, 0], [255, 255, 255]));
  else if (metricName == 'percent_responded') fill = rgbToHex(lerpColor(metric/100, [0, 0, 0], [255, 64, 64]));
  const stroke = rgbToHex(lerpColor(0.3, hexToRGB(fill), [255, 255, 255]));
  return {
    fill,
    stroke,
  }
}

function uncoloredLocData(loc: LocData): LocData {
  const { fill, stroke, ...rest } = loc;
  return rest;
}

function renderPG(loc: LocData, fovY: number, onUse) {
    const snapParams = {
    interval: scanIntervalDeg,
    offset: scanOffsetDeg - fovY - loc.y,
  };

  return <TransformGroup key={'PG '+loc.id} id={loc.id.toString()} mode='translate' snap={snapParams} onUse={onUse}>
    {elements.renderLoc(loc)}
    <text fontSize={0.8} textAnchor='middle' dominantBaseline='middle' strokeWidth={0.02} transform={`translate(${loc.x},${loc.y+1.2})`}>PG</text>
    <rect x={loc.x-1} y={loc.y-1} width={2} height={2} fill='transparent' strokeWidth={0} />
  </TransformGroup>
}

function applyEdits(locs: LocData[], edits: Map<number, Coord>) {
  return locs?.map(l => {
    const edit = edits.get(l.id);
    if (!edit) return l;
    return {
      ...l,
      x: l.x + edit.x,
      y: l.y + edit.y,
    }
  });
}


const homeGroups = [
  'BS-C',
  'BS-T',
  'BS-N',
  'BS-S',
  'BS-I',
  'GA-T',
  'GA-N',
  'GA-S',
  'GA-I',
  'HP',
  'PG',
];

const stepsNonStudy = [
  {
    action: 'idlePRL',
    confirmButton: 'Accept BS Alignment',
    editButton: 'Edit PRL',
    help: 'Examine the blindspot data for alignment with the retinal images. Edit if needed.',
    tooltip: '',
  },
  {
    action: 'translate',
    confirmButton: 'Confirm PRL Adjustment',
    // undoButton: 'Reset PRL',
    help: 'Move the imaging layer to balance the sensitivity data between opposing BS arrays. Each margin line should mark a transition from low to higher sensitivity.',
    tooltip: 'Click and drag the blindspot region to move the imaging layer.',
  },
  {
    action: 'idleArrays',
    confirmButton: 'Accept Array Placement',
    editButton: 'Edit Individual Arrays',
    help: 'Determine whether each array (home locations - white circles) should remain placed as-is. Edit if needed.',
    tooltip: '',
  },
  {
    action: 'arrays',
    confirmButton: 'Confirm Array Adjustments',
    help: 'Move any BS array to place stimuli upon a different part of the retina than previously intended. Move all desired arrays before continuing.',
    tooltip: 'Click and drag any array to move it.',
  },
  {
    action: 'pg',
    confirmButton: 'Confirm PG Locations',
    // undoButton: 'Reset PG',
    help: 'Move the peripheral (PG) locations away from blood vessels if needed.',
    tooltip: 'Click and drag a PG location to move it.',
  },
];

const stepsStudy = [
  {
    action: 'idlePRL',
    confirmButton: 'Accept GA Alignment',
    editButton: 'Edit PRL',
    help: 'Examine the GA data for alignment with the retinal images. Edit if needed.',
    tooltip: '',
  },
  {
    action: 'translate',
    confirmButton: 'Confirm PRL Adjustment',
    // undoButton: 'Reset PRL',
    help: 'Move the imaging layer to balance the sensitivity data between opposing GA arrays. Each margin line should mark a transition from low to higher sensitivity.',
    tooltip: 'Click and drag the foveal region to move the imaging layer.',
  },
  {
    action: 'idleCyclo',
    confirmButton: 'Accept BS Alignment',
    editButton: 'Edit Cyclo',
    // undoButton: 'Undo PRL',
    help: 'Examine the blindspot data for alignment with the retinal images. Edit if needed.',
    tooltip: '',
  },
  {
    action: 'rotate',
    confirmButton: 'Confirm Cyclo Adjustment',
    // undoButton: 'Reset Cyclo',
    help: 'Rotate the imaging layer to balance the sensitivity data between opposing BS arrays. Each margin line should mark a transition from low to higher sensitivity.',
    tooltip: 'Click and drag the blindspot region to rotate the imaging layer.',
  },
  {
    action: 'idleArrays',
    confirmButton: 'Accept Array Placement',
    editButton: 'Edit Individual Arrays',
    help: 'Determine whether each array (home locations - white circles) should remain placed as-is. Edit if needed.',
    tooltip: '',
  },
  {
    action: 'arrays',
    confirmButton: 'Confirm Array Adjustments',
    help: 'Move any BS or GA array to place stimuli upon a different part of the retina than previously intended. Move all desired arrays before continuing.',
    tooltip: 'Click and drag any array to move it.',
  },
  {
    action: 'mark',
    confirmButton: 'Confirm Markings',
    help: 'Mark any stimuli locations which are covered more than 50% by a blood vessel.',
    tooltip: 'Click on any stimulus location to toggle markings.',
  },
  {
    action: 'pg',
    confirmButton: 'Confirm PG Locations',
    // undoButton: 'Reset PG',
    help: 'Move the peripheral (PG) locations away from blood vessels if needed.',
    tooltip: 'Click and drag a PG location to move it.',
  },
];
